import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { fetchProjectStatuses, updateProjectStatus as updateProjectStatusApi } from '@/lib/data-service';
import type { ProjectStatusOption } from '@/lib/data-service';

/**
 * Project Status Context
 * 
 * Provides organisation-level project status labels throughout the application.
 * Statuses are fetched from the API (or use defaults in demo/tour mode) and
 * can be updated from the Settings > General page.
 */

// Status IDs that are only used for backend mapping and should not appear in user-facing dropdowns
const HIDDEN_STATUS_IDS = [4, 7]; // 4 = Reopened, 7 = Opportunity

interface ProjectStatusContextValue {
  /** Array of all project statuses with their current labels */
  statuses: ProjectStatusOption[];
  /** Array of statuses available for user selection in dropdowns (excludes Reopened & Opportunity) */
  selectableStatuses: ProjectStatusOption[];
  /** Whether statuses are currently being loaded */
  isLoading: boolean;
  /** Get the label for a given status ID */
  getStatusLabel: (statusId: number) => string;
  /** Get a record mapping status IDs to their labels */
  statusLabels: Record<number, string>;
  /** Update a status label (persists to API) */
  updateStatusLabel: (id: number, name: string) => Promise<void>;
  /** Refresh statuses from the API */
  refreshStatuses: () => Promise<void>;
}

const ProjectStatusContext = createContext<ProjectStatusContextValue | undefined>(undefined);

// Fallback labels used when context is not available or status ID is unknown
// Only includes the statuses available in the system's status picker
const FALLBACK_LABELS: Record<number, string> = {
  1: 'Planning',
  2: 'In Progress',
  3: 'On Hold',
  5: 'Cancelled',
  6: 'Completed',
};

interface ProjectStatusProviderProps {
  children: ReactNode;
}

export function ProjectStatusProvider({ children }: ProjectStatusProviderProps) {
  const [statuses, setStatuses] = useState<ProjectStatusOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadStatuses = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await fetchProjectStatuses();
      setStatuses(result);
    } catch (error) {
      console.error('Failed to load project statuses:', error);
      // Use fallback defaults
      setStatuses(
        Object.entries(FALLBACK_LABELS).map(([id, name]) => ({
          id: Number(id),
          name,
        }))
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  // Statuses available for user selection in dropdowns (excludes Reopened & Opportunity)
  const selectableStatuses = useMemo(
    () => statuses.filter(s => !HIDDEN_STATUS_IDS.includes(s.id)),
    [statuses]
  );

  const statusLabels = statuses.reduce<Record<number, string>>((acc, s) => {
    acc[s.id] = s.name;
    return acc;
  }, {});

  const getStatusLabel = useCallback(
    (statusId: number): string => {
      return statusLabels[statusId] || FALLBACK_LABELS[statusId] || `Status ${statusId}`;
    },
    [statusLabels]
  );

  const updateStatusLabel = useCallback(
    async (id: number, name: string) => {
      await updateProjectStatusApi(id, name);
      // Update local state immediately
      setStatuses(prev =>
        prev.map(s => (s.id === id ? { ...s, name } : s))
      );
    },
    []
  );

  const refreshStatuses = useCallback(async () => {
    await loadStatuses();
  }, [loadStatuses]);

  const value: ProjectStatusContextValue = {
    statuses,
    selectableStatuses,
    isLoading,
    getStatusLabel,
    statusLabels,
    updateStatusLabel,
    refreshStatuses,
  };

  return (
    <ProjectStatusContext.Provider value={value}>
      {children}
    </ProjectStatusContext.Provider>
  );
}

export function useProjectStatuses() {
  const context = useContext(ProjectStatusContext);
  if (context === undefined) {
    throw new Error('useProjectStatuses must be used within a ProjectStatusProvider');
  }
  return context;
}
